import { differenceInCalendarDays, format, startOfDay, subDays } from 'date-fns'
import { isHiddenProjectTrackerStaff } from '../../pages/project-tracker/helpers/hiddenStaff'
import { TASK_STATUS } from '../../helper/taskStatuses'
import { toSafeDate } from './ganttDates'

const PROJECT_COLOR = '#F2C94C'

const STATUS_STYLES = {
  completed: {
    label: 'Done',
    legendLabel: 'Completed',
    barColor: '#DDF8E6',
    borderColor: '#ABEFC6',
    pillColor: '#1DB954',
    textColor: '#1DB954',
  },
  in_progress: {
    label: 'In Progress',
    legendLabel: 'In Progress',
    barColor: '#DCEBFF',
    borderColor: '#B8D5FF',
    pillColor: '#2F6BCA',
    textColor: '#2F6BCA',
  },
  overdue: {
    label: 'Overdue',
    legendLabel: 'Overdue',
    barColor: '#F25555',
    borderColor: '#F25555',
    pillColor: '#FFFFFF',
    textColor: '#FFFFFF',
  },
  needs_rework: {
    label: 'Needs Rework',
    legendLabel: 'Needs Rework',
    barColor: '#FFA34D',
    borderColor: '#FFA34D',
    pillColor: '#FFFFFF',
    textColor: '#FFFFFF',
  },
  pending: {
    label: 'Pending',
    legendLabel: 'Pending',
    barColor: '#F2F4F7',
    borderColor: '#D0D5DD',
    pillColor: '#98A2B3',
    textColor: '#475467',
  },
}

const normalizeProjectStatus = (rawStatus) => {
  const status = String(rawStatus || '').toLowerCase().trim()
  if (status === 'completed' || status === 'complete') return 'completed'
  return 'ongoing'
}

const normalizeTaskStatus = (task) => {
  const rawStatus = String(task?.status || '').toLowerCase().trim()
  const dueDate = toSafeDate(task?.due_date)
  const today = startOfDay(new Date())

  if (rawStatus === TASK_STATUS.COMPLETED || rawStatus === 'done') return 'completed'
  if (rawStatus === TASK_STATUS.RETURNED_FOR_REWORK) return 'needs_rework'
  if (rawStatus === TASK_STATUS.SUBMITTED_FOR_REVIEW) return 'in_progress'
  if (rawStatus === 'overdue') return 'overdue'
  if (dueDate && differenceInCalendarDays(dueDate, today) < 0) return 'overdue'
  if (rawStatus === TASK_STATUS.PENDING) return 'pending'
  return 'in_progress'
}

const getTaskAssignee = (task) => {
  const assigned = task?.assigned_to
  if (assigned && !isHiddenProjectTrackerStaff(assigned)) {
    return {
      name: assigned?.name || 'Unknown user',
      avatarUrl:
        assigned?.staff_profile_image?.profile_image_url ||
        assigned?.profile_image_url ||
        assigned?.staff_profile_image?.profile_image ||
        '',
    }
  }

  return {
    name: task?.assignee || 'Unknown user',
    avatarUrl: '',
  }
}

const getProjectMembers = (project) =>
  (project?.members ?? [])
    .filter((member) => !isHiddenProjectTrackerStaff(member?.staff ?? member))
    .map((member) => member?.staff ?? member)
    .filter(Boolean)
    .slice(0, 3)

const getMemberAvatarUrl = (member) =>
  member?.staff_profile_image?.profile_image_url ??
  member?.profile_image_url ??
  member?.staff_profile_image?.profile_image ??
  ''

const buildProjectTimelineItem = (project) => {
  const members = getProjectMembers(project)

  return {
    type: 'project',
    start: project?.start_date,
    end: project?.deadline,
    label: project?.name || 'Untitled project',
    barColor: '#FFF7DA',
    borderColor: '#FFF1C2',
    textColor: '#B35A00',
    accentColor: PROJECT_COLOR,
    members: members.map((member) => ({
      id: member?.id,
      name: member?.name || 'User',
      avatarUrl: getMemberAvatarUrl(member),
    })),
  }
}

const buildTaskTimelineItem = (task, statusKey, type = 'current') => {
  const style = STATUS_STYLES[statusKey] ?? STATUS_STYLES.pending
  return {
    type: 'task',
    start: task?.scheduled_date,
    end: task?.scheduled_end_date,
    label: style.label,
    legendLabel: style.legendLabel,
    barColor: style.barColor,
    borderColor: style.borderColor,
    textColor: style.textColor,
    accentColor: style.pillColor,
    statusKey,
    itemType: type,
    dueDate: task?.due_date ?? null,
  }
}

const hasValidWorkingPeriod = (task) =>
  Boolean(task?.scheduled_date && task?.scheduled_end_date)

const hasValidWorkingPeriodHistory = (task) =>
  (task?.working_period_histories || []).some(
    (history) => history?.scheduled_date && history?.scheduled_end_date
  )

const isLegacyTaskEligibleForFallback = (task) => {
  const normalizedStatus = String(task?.status || '').toLowerCase().trim()
  const isCompletedLike = normalizedStatus === TASK_STATUS.COMPLETED || normalizedStatus === 'done'
  const isSubmittedForReview = normalizedStatus === TASK_STATUS.SUBMITTED_FOR_REVIEW

  return (
    (isCompletedLike || isSubmittedForReview) &&
    Boolean(task?.due_date) &&
    !hasValidWorkingPeriod(task) &&
    !hasValidWorkingPeriodHistory(task)
  )
}

const buildFallbackTimelineItem = (task) => {
  const dueDate = toSafeDate(task?.due_date)
  if (!dueDate) return null

  const fallbackEnd = startOfDay(dueDate)
  const fallbackStart = subDays(fallbackEnd, 2)
  const fallbackStatusKey = normalizeTaskStatus(task)

  return {
    id: `fallback-${task.id}`,
    ...buildTaskTimelineItem(
      {
        ...task,
        scheduled_date: format(fallbackStart, 'yyyy-MM-dd'),
        scheduled_end_date: format(fallbackEnd, 'yyyy-MM-dd'),
      },
      fallbackStatusKey,
      'fallback'
    ),
    isDerivedFallback: true,
    fallbackSource: 'due_date',
  }
}

const buildTaskTimelineItems = (task) => {
  const historyItems = (task?.working_period_histories || [])
    .filter((history) => history?.scheduled_date && history?.scheduled_end_date)
    .map((history) => {
      const statusKey = normalizeTaskStatus(history)
      return {
        id: `history-${history.id}`,
        historyId: history.id,
        ...buildTaskTimelineItem(history, statusKey, 'history'),
      }
    })

  const currentStatusKey = normalizeTaskStatus(task)
  const currentItem =
    task?.scheduled_date && task?.scheduled_end_date
      ? {
          id: `current-${task.id}`,
          ...buildTaskTimelineItem(task, currentStatusKey, 'current'),
        }
      : null

  const fallbackItem =
    !historyItems.length &&
    !currentItem &&
    isLegacyTaskEligibleForFallback(task)
      ? buildFallbackTimelineItem(task)
      : null

  return [...historyItems, currentItem, fallbackItem]
    .filter(Boolean)
    .sort((a, b) => {
      const aStart = toSafeDate(a?.start)?.getTime() ?? Number.POSITIVE_INFINITY
      const bStart = toSafeDate(b?.start)?.getTime() ?? Number.POSITIVE_INFINITY
      if (aStart !== bStart) return aStart - bStart
      if (a.itemType === b.itemType) return 0
      return a.itemType === 'history' ? -1 : 1
    })
}

const getGanttTaskSortPriority = (task) => {
  const status = normalizeTaskStatus(task)
  if (status === 'completed') return 2
  return 1
}

export const buildGanttProjectTree = ({ projects = [], tasks = [] }) => {
  const tasksByProjectId = new Map()
  const legacyTasks = []

  tasks.forEach((task) => {
    if (!task?.project_id) return
    const timelineItems = buildTaskTimelineItems(task)
    if (!timelineItems.length) {
      legacyTasks.push(task)
      return
    }

    if (!tasksByProjectId.has(task.project_id)) tasksByProjectId.set(task.project_id, [])
    tasksByProjectId.get(task.project_id).push(task)
  })

  const projectTree = (projects || []).map((project) => {
    const normalizedProjectStatus = normalizeProjectStatus(project?.status)
    const rawTasks = tasksByProjectId.get(project.id) ?? []

    const mappedTasks = rawTasks
      .map((task) => {
        const statusKey = normalizeTaskStatus(task)
        const assignee = getTaskAssignee(task)
        const timelineItems = buildTaskTimelineItems(task)
        const primaryTimelineItem =
          timelineItems.find((item) => item.itemType === 'current') ||
          timelineItems[timelineItems.length - 1] ||
          null

        return {
          ...task,
          projectName: project?.name || 'Untitled project',
          assigneeName: assignee.name,
          assigneeAvatarUrl: assignee.avatarUrl,
          deliverableName: task?.project_member_deliverable?.deliverable_type || '',
          statusKey,
          statusLabel: primaryTimelineItem?.label || (STATUS_STYLES[statusKey] ?? STATUS_STYLES.pending).label,
          timelineItem: primaryTimelineItem,
          timelineItems,
        }
      })
      .sort((a, b) => {
        const priorityDiff = getGanttTaskSortPriority(a) - getGanttTaskSortPriority(b)
        if (priorityDiff !== 0) return priorityDiff

        const aTime =
          toSafeDate(a?.timelineItems?.[0]?.start)?.getTime() ??
          toSafeDate(a?.scheduled_date)?.getTime() ??
          Number.POSITIVE_INFINITY
        const bTime =
          toSafeDate(b?.timelineItems?.[0]?.start)?.getTime() ??
          toSafeDate(b?.scheduled_date)?.getTime() ??
          Number.POSITIVE_INFINITY
        if (aTime !== bTime) return aTime - bTime

        const aCreatedAt = toSafeDate(a?.created_at)?.getTime() ?? 0
        const bCreatedAt = toSafeDate(b?.created_at)?.getTime() ?? 0
        return bCreatedAt - aCreatedAt
      })

    return {
      ...project,
      normalizedProjectStatus,
      timelineItem: buildProjectTimelineItem(project),
      tasks: mappedTasks,
      visibleTasks: mappedTasks,
    }
  })

  return {
    projectTree,
    legacyTasks,
  }
}
